import { Component, Input, OnChanges, ViewChildren, QueryList, Output, EventEmitter, SimpleChanges, Injector, OnInit } from '@angular/core';
import { ChecklistTaskComponent } from './checklist-task/checklist-task.component';
import { ChecklistsService } from '../../checklists.service';
import { Checklist, ChecklistTemplate } from '../../checklists';
import { FormStatusEnum } from 'src/app/common/enumerations/enumerations';
import { WfTaskLocalResource } from 'src/app/components/workflow/workflow';
import { utils } from 'src/app/modules/libs/utils';
import { Observable, Subscription } from 'rxjs';
import { BaseComponent } from 'src/app/common/base/base.component';
import { ConditionParser } from 'src/app/modules/libs/condition-parser';

@Component({
  selector: 'checklist-tasks',
  templateUrl: './checklist-tasks.component.html',
  styleUrls: ['./checklist-tasks.component.scss']
})
export class ChecklistTasksComponent extends BaseComponent implements OnInit, OnChanges {

  @Input() tasks!: WfTaskLocalResource[];
  @Input() currentChecklist?: Checklist | null;
  @Input() currentChecklistTemplate?: ChecklistTemplate;
  @Input() disabled!: boolean;
  @Input() precheck?: boolean;

  @Output() checkShared = new EventEmitter<any>();
  @Output() taskChanged = new EventEmitter<WfTaskLocalResource>();

  @ViewChildren(ChecklistTaskComponent) taskComponents!: QueryList<ChecklistTaskComponent>;

  wfTaskLocal?: WfTaskLocalResource;
  wfTaskLocals?: WfTaskLocalResource[];
  wfTaskLocals$!: Observable<WfTaskLocalResource[]>;
  wfTaskLocalsSubs!: Subscription;

  formStatus = FormStatusEnum;
  updating!: boolean;
  renumbering!: boolean;
  update!: boolean;
  private itemCountMap = new Map<number, number>();

  constructor(
    protected override injector: Injector,
    private checklistService: ChecklistsService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.loadWFTaskLocals();
  }

  /**
   * Loads checklist execution tasks and applies their persisted condition state.
   */
  loadWFTaskLocals() {
    this.wfTaskLocals$ = this.store.select(state => state.WFTaskLocals.data);
    this.wfTaskLocalsSubs = this.wfTaskLocals$.subscribe(data => {
      if (data?.length) {
        this.wfTaskLocals = data
          .filter(t => t.wfStepLocal?.wfSteps?.at(-1)?.wfTableID === this.currentChecklist?.wfTableID)
          .sort((a, b) =>
            (a.wfStepLocal?.wfSectionLocal?.number ?? 0) - (b.wfStepLocal?.wfSectionLocal?.number ?? 0) ||
            (a.wfStepLocal?.order ?? 0) - (b.wfStepLocal?.order ?? 0) ||
            (a.order ?? 0) - (b.order ?? 0)
          );

        this.wfTaskLocals.map(t => {
          t.isCompleted = this.utils.taskComplete(t);
          t.previousTasksCompleted = this.previousTasksCompleted(t);
          t.sigConditionResult = this.sigCondition(t);
        });

        ConditionParser.parseConditions(this.wfTaskLocals);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.updating && !this.renumbering) {
      this.renumber();
    }
  }

  /**
   * Renumbers required numeric tasks while reserving extra numbers for dynamic controls.
   */
  renumber() {
    this.tasks = this.tasks.sort((a, b) => utils.sort(a.order, b.order));
    let number = 1;
    this.tasks.map(t => {
      if (t.required && t.component?.numericHeader) {
        if (t.name != number.toString()) {
          t.name = number.toString();
        }
        // Dynamic components render several numbered rows, so following tasks must start after their span.
        const span = t.id ? this.itemCountMap.get(t.id) ?? t.itemCount ?? 1 : t.itemCount ?? 1;
        number += Math.max(span, 1);
      }
    });
  }

  /**
   * Updates the dynamic row span for a task and refreshes rendered task numbers.
   * @param task Task that reported the rendered item count.
   * @param count Number of numbered rows rendered by the task component.
   */
  onItemCountChanged(task: WfTaskLocalResource, count: number): void {
    if (!task.id || this.itemCountMap.get(task.id) === count) return;
    this.itemCountMap.set(task.id, count);
    task.itemCount = count;
    this.renumber();
    this.taskComponents?.forEach(component => component.refreshSignature(component.task));
  }

  /**
   * Replaces a saved task and re-evaluates conditional siblings using the persisted value.
   * @param task Task returned by the checklist save operation.
   */
  updateTask(task: WfTaskLocalResource): void {
    this.replaceTask(task);
    this.refreshTaskConditions();
    // Bubble persisted changes so conditions spanning sections can be re-evaluated.
    this.taskChanged.emit(task);
  }

  /**
   * Applies a locally selected value so conditional siblings update before persistence completes.
   * @param task Task whose value changed in the checklist UI.
   */
  updateTaskConditions(task: WfTaskLocalResource): void {
    this.replaceTask(task);
    this.refreshTaskConditions();
    // Bubble local value changes so dependent tasks outside this step update immediately.
    this.taskChanged.emit(task);
  }

  /**
   * Preserves the shared task array reference while replacing one task value.
   * @param task Updated task to place in the section task collection.
   */
  private replaceTask(task: WfTaskLocalResource): void {
    const taskIndex = this.tasks.findIndex(currentTask => currentTask.id === task.id);
    if (taskIndex >= 0)
      this.tasks.splice(taskIndex, 1, task);
  }

  /**
   * Re-evaluates task conditions and refreshes rendered signatures without reloading the checklist.
   */
  private refreshTaskConditions(): void {
    // Keep conditional visibility and disabled state synchronized across sibling task components.
    ConditionParser.parseConditions(this.tasks);
    this.taskComponents?.forEach(component => {
      const task = this.tasks.find(currentTask => currentTask.id === component.task.id);
      if (task)
        component.refreshSignature(task);
    });
    this.renumber();
  }

  /**
   * Refreshes rendered task rows after parent-level condition evaluation updates task state.
   */
  refreshRenderedTasks(): void {
    this.taskComponents?.forEach(component => {
      const task = this.tasks.find(currentTask => currentTask.id === component.task.id);
      if (task)
        component.refreshSignature(task);
    });
    this.renumber();
  }

  trackByTaskId(_index: number, task: WfTaskLocalResource): number {
    return task.id!;
  }

  sigCondition(t: WfTaskLocalResource) {
    if (t.sigCondition) {
      return this.wfTaskLocals?.find(x => x.code == t.sigCondition)?.wfTasks?.[0]?.signedBy?.id == this.currentUser?.id;
    }
    else return false;
  }

  previousTasksCompleted(task: WfTaskLocalResource): boolean {
    const tasks = this.wfTaskLocals
      ?.filter(t => t?.required && t.wfTasks?.at(-1)?.enabled && (t.component?.type ?? 0) !== 2) as WfTaskLocalResource[] | undefined;

    if (!tasks?.length) {
      return false;
    }

    const currentIndex = tasks.findIndex(t => t.id === task?.id);

    if (currentIndex <= 0) {
      return true; // first task has no previous tasks
    }

    return tasks
      .slice(0, currentIndex)
      .every(t => t.isCompleted);
  }
}
